import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TiptapEditorComponent } from '../tiptap-editor';
import { AiNotesService, AiNotesStreamEvent } from './ai-notes.service';

/**
 * Initial seed handed to the overlay when it opens. Carries everything needed
 * to kick off the first generation: the user's prompt, whether the source
 * notes should be included as context, the source notes HTML itself, and a
 * `hasNotesContent` flag that drives the apply-button variants.
 */
export interface OverlayOpenInput {
    prompt: string;
    useNotesAsContext: boolean;
    sourceNotesHtml: string;
    hasNotesContent: boolean;
}

/**
 * What `snapshot()` hands a host and `resumeFrom` takes back: enough for a NEW
 * instance to carry on as the destroyed one did - the seed the draft answers,
 * the draft itself, the prompt chip and the refine box, and whether a request
 * was still running (`interrupted`), which the new instance re-issues.
 */
export interface WriteWithAiOverlaySnapshot {
    seed: OverlayOpenInput;
    /** Settled phase to come back in. A busy overlay snapshots as 'ready' with
     *  `interrupted` set, and the re-issued request takes it from there. */
    state: 'ready' | 'error';
    interrupted: boolean;
    /** Mid-request this is the draft the request STARTED from, not the
     *  half-streamed answer - it is what the re-issued request needs. */
    draftHtml: string;
    seedPrompt: string;
    seedPromptChipVisible: boolean;
    refinePrompt: string;
    errorMessage: string;
    lastOperation: 'generate' | 'refine';
    lastRefineInstruction: string;
}

/** Phases the overlay can be in.
 *   'generating' — waiting for the first streamed token; shows skeleton.
 *   'streaming'  — tokens arriving; editor visible and accumulating content.
 *   'ready'      — stream complete; user can refine, edit, or apply.
 *   'error'      — request failed; "Try again" available. */
type OverlayState = 'generating' | 'streaming' | 'ready' | 'error';

@Component({
    selector: 'vf-write-with-ai-overlay',
    templateUrl: './write-with-ai-overlay.component.html',
    styleUrls: ['./write-with-ai-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WriteWithAiOverlayComponent implements OnChanges, OnDestroy {

    @Input() open: boolean = false;
    /**
     * Where a freshly CREATED instance picks up - see `snapshot()`. A host that
     * destroys the overlay with a tab switch (an `*ngIf` round the notes pane)
     * hands back what `snapshot()` gave it, so the new instance shows the same
     * draft instead of the skeleton forever: the seed lived in the instance
     * that died, and a fresh one has nothing to generate from (user's finding
     * in the task composer, 2026-09-15). Read once, on the first change - a
     * later change while this instance is alive is the host taking a NEW
     * snapshot of THIS instance, and there is nothing to restore from it.
     */
    @Input() resumeFrom: WriteWithAiOverlaySnapshot | null = null;

    @Output() closed: EventEmitter<void> = new EventEmitter<void>();
    @Output() addToNotes: EventEmitter<string> = new EventEmitter<string>();
    @Output() insertBelowNotes: EventEmitter<string> = new EventEmitter<string>();
    @Output() insertAboveNotes: EventEmitter<string> = new EventEmitter<string>();
    @Output() replaceNotes: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('draftEditor', { static: false }) draftEditor!: TiptapEditorComponent;
    @ViewChild('refineInput', { static: false }) refineInput!: ElementRef<HTMLTextAreaElement>;

    public state: OverlayState = 'generating';
    public draftHtml: string = '';
    public refinePrompt: string = '';
    public seedPrompt: string = '';
    public seedPromptChipVisible: boolean = true;
    public errorMessage: string = '';
    public hasNotesContent: boolean = false;

    public showApplyDropdown: boolean = false;
    public showDiscardConfirm: boolean = false;

    private _seed: OverlayOpenInput | null = null;
    private _currentSub: Subscription | null = null;
    /** Tracks which call type errored so "Try again" can rerun the same op
     *  instead of always restarting from the initial prompt. */
    private _lastOperation: 'generate' | 'refine' = 'generate';
    private _lastRefineInstruction: string = '';
    /** The draft the current request started from ('' for a generate), kept
     *  for `snapshot()`: mid-request the editor holds a half-streamed answer. */
    private _draftBeforeRequest: string = '';
    /** Accumulated HTML for the current in-flight stream. Cleared at the
     *  start of each call. While streaming, we push this into the editor on
     *  each delta so the user sees content build up token-by-token. */
    private _streamBuffer: string = '';
    /** Throttle handle for setValue updates — flushing to the editor on every
     *  single token kills typing latency (each setValue rebuilds the doc).
     *  We coalesce frames into ~16ms ticks for smooth animation. */
    private _streamFlushHandle: any = null;
    private _streamFlushPending: boolean = false;

    constructor(
        private _aiService: AiNotesService,
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        const resume = changes['resumeFrom'];
        if (resume && resume.firstChange && resume.currentValue) {
            this._resume(resume.currentValue as WriteWithAiOverlaySnapshot);
        }
    }

    ngOnDestroy(): void {
        this._cancelCurrentRequest();
        this._cancelPendingFlush();
    }

    // ── Public lifecycle ──

    /**
     * Host calls this after `open` is flipped to true to seed the overlay and
     * fire the initial generation. Resets internal state for the new session.
     */
    public start(input: OverlayOpenInput): void {
        this._seed = input;
        this.seedPrompt = input.prompt;
        this.seedPromptChipVisible = true;
        this.hasNotesContent = input.hasNotesContent;
        this.draftHtml = '';
        this.refinePrompt = '';
        this.showApplyDropdown = false;
        this.showDiscardConfirm = false;
        this.errorMessage = '';
        this.state = 'generating';
        this._cdr.markForCheck();
        this._generate();
    }

    /**
     * Everything a host needs to bring a rebuilt instance back to this one's
     * state through `resumeFrom`. Null before `start()` has seeded the overlay:
     * there is nothing to come back to, and the host should not reopen one.
     */
    public snapshot(): WriteWithAiOverlaySnapshot | null {
        if (!this._seed) {
            return null;
        }
        const busy: boolean = this.isAiBusy;
        return {
            seed: this._seed,
            state: this.state === 'error' ? 'error' : 'ready',
            interrupted: busy,
            draftHtml: busy ? this._draftBeforeRequest : this._readEditorHtml(),
            seedPrompt: this.seedPrompt,
            seedPromptChipVisible: this.seedPromptChipVisible,
            refinePrompt: this.refinePrompt,
            errorMessage: this.errorMessage,
            lastOperation: this._lastOperation,
            lastRefineInstruction: this._lastRefineInstruction
        };
    }

    /** The other half of `snapshot()`, run from `ngOnChanges` before the first
     *  render, so the draft is there from the first frame. A request that was
     *  running when the old instance died is re-issued the way "Try again"
     *  does it - the user asked and never got the answer. */
    private _resume(snapshot: WriteWithAiOverlaySnapshot): void {
        this._seed = snapshot.seed;
        this.hasNotesContent = snapshot.seed.hasNotesContent;
        this.seedPrompt = snapshot.seedPrompt;
        this.seedPromptChipVisible = snapshot.seedPromptChipVisible;
        this.refinePrompt = snapshot.refinePrompt;
        this.errorMessage = snapshot.errorMessage;
        this._lastOperation = snapshot.lastOperation;
        this._lastRefineInstruction = snapshot.lastRefineInstruction;
        this._draftBeforeRequest = snapshot.draftHtml;
        this.draftHtml = snapshot.draftHtml;
        this.state = snapshot.state;
        this.showApplyDropdown = false;
        this.showDiscardConfirm = false;
        this._cdr.markForCheck();
        if (snapshot.interrupted) {
            // No editor exists yet, so the refine path reads `draftHtml` - the
            // draft the interrupted request started from - which is the point.
            this.tryAgain();
        }
    }

    // ── Generation ──

    private _generate(): void {
        if (!this._seed) {
            return;
        }
        this._cancelCurrentRequest();
        this._lastOperation = 'generate';
        this._draftBeforeRequest = '';
        this._beginStream();
        this._currentSub = this._aiService.generateStream({
            prompt: this._seed.prompt,
            sourceNotesHtml: this._seed.sourceNotesHtml,
            useNotesAsContext: this._seed.useNotesAsContext
        }).subscribe(
            event => this._onStreamEvent(event),
            err => this._onAiResponse('', true, (err && err.message) || 'AI request failed.')
        );
    }

    private _refine(instructionOverride?: string): void {
        let instruction: string = (instructionOverride !== undefined && instructionOverride !== null)
            ? instructionOverride
            : (this.refinePrompt || '').trim();
        if (!instruction) {
            return;
        }
        this._cancelCurrentRequest();
        let currentDraft: string = this._readEditorHtml();
        this._draftBeforeRequest = currentDraft;
        this.state = 'generating';
        // Show the latest user instruction in the prompt chip so the user
        // always sees what the current draft is responding to, not the
        // initial Ask-AI prompt.
        this.seedPrompt = instruction;
        this.seedPromptChipVisible = true;
        this._lastOperation = 'refine';
        this._lastRefineInstruction = instruction;
        // Only clear the input when the call originated from the user (not from
        // a retry that already has the instruction stashed).
        if (instructionOverride === undefined || instructionOverride === null) {
            this.refinePrompt = '';
        }
        this._cdr.markForCheck();
        // Carry the original seed forward so refinements stay grounded in the
        // user's source notes (when the callout toggle was ON) and aligned
        // with the initial Ask-AI prompt's overall goal — preventing drift
        // across multiple refine iterations.
        this._beginStream();
        this._currentSub = this._aiService.refineStream({
            instruction,
            currentDraftHtml: currentDraft,
            sourceNotesHtml: this._seed ? this._seed.sourceNotesHtml : '',
            useNotesAsContext: this._seed ? this._seed.useNotesAsContext : false,
            originalPrompt: this._seed ? this._seed.prompt : ''
        }).subscribe(
            event => this._onStreamEvent(event),
            err => this._onAiResponse('', true, (err && err.message) || 'AI refinement failed.')
        );
    }

    /** Retry whichever operation last errored. "Try again" in the error
     *  banner calls this instead of redo() so a failed refine doesn't lose
     *  the user's refinement instruction and silently reset to the initial
     *  prompt. */
    public tryAgain(): void {
        if (this._lastOperation === 'refine' && this._lastRefineInstruction) {
            this._refine(this._lastRefineInstruction);
        } else {
            this._generate();
        }
    }

    private _onAiResponse(html: string, isError: boolean, errorMessage: string): void {
        if (isError) {
            this.state = 'error';
            this.errorMessage = errorMessage || 'Something went wrong. Try again.';
            this._cdr.markForCheck();
            return;
        }
        this.draftHtml = html || '';
        this.state = 'ready';
        this._cdr.markForCheck();
        setTimeout(() => {
            if (this.draftEditor) {
                this.draftEditor.setValue(this.draftHtml);
            }
        }, 0);
    }

    // ── Streaming consumption ──

    /**
     * Resets the stream buffer + state and flips to the 'generating' phase so
     * the skeleton shows until the first token lands. Called at the top of
     * both `_generate` and `_refine`.
     */
    private _beginStream(): void {
        this._cancelPendingFlush();
        this._streamBuffer = '';
        this.draftHtml = '';
        this.state = 'generating';
        this.errorMessage = '';
        this._cdr.markForCheck();
    }

    /**
     * Handles a single streamed event from the AI service. Three branches:
     *  - delta: accumulate + schedule an editor flush (throttled to one paint
     *    per ~16ms so token storms don't murder the renderer).
     *  - isError: surface in the error banner via _onAiResponse.
     *  - done: flush any remaining buffer + transition to 'ready'.
     */
    private _onStreamEvent(event: AiNotesStreamEvent): void {
        if (!event) {
            return;
        }
        if (event.isError) {
            this._cancelPendingFlush();
            this._onAiResponse('', true, event.errorMessage || 'AI request failed.');
            return;
        }
        if (event.done) {
            this._cancelPendingFlush();
            this._flushStream();
            this.draftHtml = this._streamBuffer;
            this.state = 'ready';
            this._cdr.markForCheck();
            return;
        }
        if (typeof event.delta === 'string' && event.delta.length > 0) {
            this._streamBuffer += event.delta;
            if (this.state === 'generating') {
                this.state = 'streaming';
                this._cdr.markForCheck();
            }
            this._scheduleFlush();
        }
    }

    /** Coalesces flushes to one per animation frame so a flurry of tokens
     *  results in one editor update instead of dozens. */
    private _scheduleFlush(): void {
        if (this._streamFlushPending) {
            return;
        }
        this._streamFlushPending = true;
        this._streamFlushHandle = (typeof requestAnimationFrame === 'function')
            ? requestAnimationFrame(() => this._flushStream())
            : setTimeout(() => this._flushStream(), 16);
    }

    private _flushStream(): void {
        this._streamFlushPending = false;
        this._streamFlushHandle = null;
        if (this.draftEditor) {
            this.draftEditor.setValue(this._streamBuffer);
        }
        this.draftHtml = this._streamBuffer;
    }

    private _cancelPendingFlush(): void {
        if (this._streamFlushHandle !== null) {
            if (typeof cancelAnimationFrame === 'function' && typeof requestAnimationFrame === 'function') {
                try { cancelAnimationFrame(this._streamFlushHandle); } catch { /* noop */ }
            } else {
                try { clearTimeout(this._streamFlushHandle); } catch { /* noop */ }
            }
            this._streamFlushHandle = null;
        }
        this._streamFlushPending = false;
    }

    // ── Editor interactions ──

    public onEditorValueChange(ev: { value: string; bubbles: boolean }): void {
        if (!ev) {
            return;
        }
        if (!ev.bubbles) {
            this.draftHtml = ev.value || '';
        }
    }

    /** Bubble-toolbar Proofread inside the overlay's editor. Mirrors the
     *  main notes editor's inline proofread flow:
     *   1. Snapshot the full HTML so Undo can restore exactly.
     *   2. Show the "Proofreading…" progress pill on the editor.
     *   3. Call the dedicated leaner AI proofread endpoint (GPT-5) with the
     *      selected text (or whole draft if nothing is selected).
     *   4. Replace inline on success, swap pill to "1 change to review".
     *   5. Hide the progress pill on error so the user isn't stuck.
     *
     *  We intentionally do NOT flip the overlay's `state` to 'generating'
     *  here — that would swap the editor for the full-area skeleton and
     *  block the user from seeing what's being corrected. Proofread is an
     *  in-place edit; the lightweight pill is the right indicator. */
    public onOverlayProofread(): void {
        if (!this.draftEditor) {
            return;
        }
        let beforeFull: string = this.draftEditor.getHtml() || '';
        let range = this.draftEditor.getSelectionRange();
        let target: string = '';
        let usingSelection: boolean = false;
        if (range) {
            // Whole lines in, whole lines out — see the same widening in the notes' inline proofread. A
            // selection that stopped mid-item left its unselected head behind as an extra list item.
            range = this.draftEditor.expandRangeToBlocks(range.from, range.to);
            target = this.draftEditor.getRangeHtml(range.from, range.to);
            usingSelection = true;
        } else {
            target = beforeFull;
        }
        if (!target || !target.replace(/<[^>]*>/g, '').trim()) {
            return;
        }
        this._cancelCurrentRequest();
        this.draftEditor.showProofreadProgress();
        this._currentSub = this._aiService.proofread(target).subscribe(
            result => {
                if (!this.draftEditor) {
                    return;
                }
                if (result.isError) {
                    this.draftEditor.hideProofreadProgress();
                    return;
                }
                if (usingSelection && range) {
                    this.draftEditor.replaceRange(range.from, range.to, result.html || '');
                } else {
                    this.draftEditor.setValue(result.html || '');
                }
                this.draftHtml = this.draftEditor.getHtml();
                // Editor diffs against its current HTML to compute the count.
                this.draftEditor.showProofreadReview(beforeFull);
            },
            _err => {
                if (this.draftEditor) {
                    this.draftEditor.hideProofreadProgress();
                }
            }
        );
    }

    // ── Refinement input ──

    public onRefineKeydown(ev: KeyboardEvent): void {
        if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault();
            this.submitRefinement();
        }
    }

    public submitRefinement(): void {
        if (this.isAiBusy) {
            return;
        }
        let instruction: string = (this.refinePrompt || '').trim();
        if (!instruction) {
            return;
        }
        this._refine();
    }

    public get canSubmitRefine(): boolean {
        return !this.isAiBusy && (this.refinePrompt || '').trim().length > 0;
    }

    /** True while AI is generating or streaming — used by template + redo/
     *  refine guards. Streaming counts as "busy" because the draft isn't
     *  complete yet; letting the user click Redo mid-stream would race the
     *  in-flight request. */
    public get isAiBusy(): boolean {
        return this.state === 'generating' || this.state === 'streaming';
    }

    /** True when there's already a non-empty draft in the editor. Used by
     *  the template to keep the editor visible during refinement (state
     *  goes back to 'generating' for a refine call but we don't want to
     *  blank the existing draft — the "Generating" pill is enough). */
    public get hasDraftContent(): boolean {
        const html: string = this.draftHtml || '';
        // Strip tags to detect "visually empty" HTML (e.g. <p></p>).
        return html.replace(/<[^>]*>/g, '').trim().length > 0;
    }

    // ── Redo (regenerate initial draft) ──

    public redo(): void {
        if (this.isAiBusy || !this._seed) {
            return;
        }
        // Restore the chip to the original Ask-AI prompt — redo re-runs the
        // initial generation, so the latest refinement instruction (if any)
        // no longer applies.
        this.seedPrompt = this._seed.prompt;
        this.seedPromptChipVisible = true;
        this._generate();
    }

    public dismissPromptChip(): void {
        this.seedPromptChipVisible = false;
    }

    // ── Apply ──

    public toggleApplyDropdown(): void {
        this.showApplyDropdown = !this.showApplyDropdown;
    }

    public closeApplyDropdown(): void {
        this.showApplyDropdown = false;
    }

    public applyPrimary(): void {
        if (this.state !== 'ready') {
            return;
        }
        let html: string = this._readEditorHtml();
        if (!this.hasNotesContent) {
            this.addToNotes.emit(html);
        } else {
            this.insertBelowNotes.emit(html);
        }
        this._afterApply();
    }

    /** Replace notes — immediate, no second-click confirmation. */
    public applyReplaceClicked(): void {
        this.showApplyDropdown = false;
        if (this.state !== 'ready') {
            return;
        }
        let html: string = this._readEditorHtml();
        this.replaceNotes.emit(html);
        this._afterApply();
    }

    /** Insert above notes — emits the secondary "prepend before existing
     *  notes" action from the apply dropdown. Symmetric to the primary
     *  "Insert below notes" action; the host handler differs only in where
     *  the AI content lands relative to the current note. */
    public applyInsertAboveClicked(): void {
        this.showApplyDropdown = false;
        if (this.state !== 'ready') {
            return;
        }
        let html: string = this._readEditorHtml();
        this.insertAboveNotes.emit(html);
        this._afterApply();
    }

    private _afterApply(): void {
        this.draftHtml = '';
        this.closed.emit();
    }

    public get applyPrimaryLabel(): string {
        if (!this.hasNotesContent) {
            return 'Add to notes';
        }
        return 'Insert below notes';
    }

    public get showApplyCaret(): boolean {
        return this.hasNotesContent;
    }

    // ── Close + discard ──

    public requestClose(): void {
        if (this._hasUnsavedDraft()) {
            this.showDiscardConfirm = true;
            this._cdr.markForCheck();
            return;
        }
        this._performClose();
    }

    public confirmDiscard(): void {
        this.showDiscardConfirm = false;
        this._performClose();
    }

    public cancelDiscard(): void {
        this.showDiscardConfirm = false;
        this._cdr.markForCheck();
    }

    private _performClose(): void {
        this._cancelCurrentRequest();
        this.draftHtml = '';
        this.refinePrompt = '';
        this.closed.emit();
    }

    @HostListener('document:keydown', ['$event'])
    public onKeydown(ev: KeyboardEvent): void {
        if (!this.open) {
            return;
        }
        if (this.showDiscardConfirm) {
            if (ev.key === 'Escape') {
                ev.preventDefault();
                this.cancelDiscard();
            }
            return;
        }
        if (ev.key === 'Escape') {
            ev.preventDefault();
            this.requestClose();
            return;
        }
        if ((ev.metaKey || ev.ctrlKey) && ev.key === 'Enter') {
            // Apply the primary visible action — only when ready and the
            // input that has focus is not the refine textarea (Ctrl/Cmd+Enter
            // there submits the refinement instead).
            let active: Element | null = document.activeElement;
            if (this.refineInput && active === this.refineInput.nativeElement) {
                ev.preventDefault();
                this.submitRefinement();
                return;
            }
            if (this.state === 'ready') {
                ev.preventDefault();
                this.applyPrimary();
            }
        }
    }

    // ── Internals ──

    private _readEditorHtml(): string {
        if (this.draftEditor) {
            return this.draftEditor.getHtml() || '';
        }
        return this.draftHtml || '';
    }

    private _hasUnsavedDraft(): boolean {
        let html: string = this._readEditorHtml();
        if (!html) {
            return false;
        }
        let stripped: string = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return stripped.length > 0;
    }

    private _cancelCurrentRequest(): void {
        if (this._currentSub) {
            this._currentSub.unsubscribe();
            this._currentSub = null;
        }
    }
}
